import React, { useEffect, useState, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CalendarIcon, MessageSquareIcon, GripVerticalIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Condition, ASSIGNEES, ASSIGNEE_COLORS } from '../types/condition';
import { CategoryBadge } from './CategoryBadge';
type ConditionCardProps = {
  condition: Condition;
  isDragging?: boolean;
  onUpdateAssignee?: (id: string, assignee: string) => void;
};
function getDueDateStatus(dueDate: string): {
  label: string;
  className: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return {
      label: `${Math.abs(diffDays)}d overdue`,
      className: 'text-red-600 bg-red-50'
    };
  } else if (diffDays === 0) {
    return {
      label: 'Due today',
      className: 'text-amber-600 bg-amber-50'
    };
  } else if (diffDays <= 3) {
    return {
      label: `Due in ${diffDays}d`,
      className: 'text-amber-600 bg-amber-50'
    };
  } else {
    return {
      label: formatDate(dueDate),
      className: 'text-gray-500 bg-gray-50'
    };
  }
}
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}
function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
export function ConditionCard({
  condition,
  isDragging,
  onUpdateAssignee
}: ConditionCardProps) {
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({
    id: condition.id
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  const dueDateStatus = getDueDateStatus(condition.dueDate);
  const isActuallyDragging = isDragging || isSortableDragging;
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsEditingAssignee(false);
      }
    }
    if (isEditingAssignee) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditingAssignee]);
  const handleAssigneeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditingAssignee(!isEditingAssignee);
  };
  const handleSelectAssignee = (assignee: string) => {
    if (onUpdateAssignee) {
      onUpdateAssignee(condition.id, assignee);
    }
    setIsEditingAssignee(false);
  };
  return <div ref={setNodeRef} style={style} className={`
        group bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing
        transition-all duration-200 select-none relative
        ${isActuallyDragging ? 'shadow-xl ring-2 ring-blue-400 ring-opacity-50 opacity-90 scale-[1.02] rotate-1' : 'shadow-sm hover:shadow-md hover:border-gray-300'}
      `} {...attributes} {...listeners} data-id="element-96">
      {/* Priority Indicator */}
      {condition.priority === 'high' && <div className="absolute top-0 left-0 w-1 h-full bg-red-400 rounded-l-lg" aria-hidden="true" data-id="element-97" />}

      {/* Header: Category + Drag Handle */}
      <div className="flex items-start justify-between mb-2" data-id="element-98">
        <CategoryBadge category={condition.category} data-id="element-99" />
        <GripVerticalIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0" aria-hidden="true" data-id="element-100" />
      </div>

      {/* Title */}
      <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-3 leading-snug" data-id="element-101">
        {condition.title}
      </h3>

      {/* Footer: Due Date, Notes, Assignee */}
      <div className="flex items-center justify-between" data-id="element-102">
        <div className="flex items-center gap-2" data-id="element-103">
          {/* Due Date */}
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${dueDateStatus.className}`} data-id="element-104">
            <CalendarIcon className="w-3 h-3" aria-hidden="true" data-id="element-105" />
            {dueDateStatus.label}
          </span>

          {/* Notes Indicator */}
          {condition.notes && <span className="inline-flex items-center gap-0.5 text-xs text-gray-400" data-id="element-106">
              <MessageSquareIcon className="w-3 h-3" aria-hidden="true" data-id="element-107" />
            </span>}
        </div>

        {/* Assignee Avatar - Inline Editable */}
        <div className="relative" ref={dropdownRef} data-id="element-108">
          <button onClick={handleAssigneeClick} onPointerDown={e => e.stopPropagation()} className={`
              w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white
              ${ASSIGNEE_COLORS[condition.assignee] || 'bg-gray-400'}
              hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-all
              focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
            `} title={`Assigned to ${condition.assignee} - Click to change`} aria-label={`Assigned to ${condition.assignee}. Click to change assignee.`} aria-expanded={isEditingAssignee} aria-haspopup="listbox" data-id="element-109">
            {getInitials(condition.assignee)}
          </button>

          {/* Assignee Dropdown */}
          {isEditingAssignee && <div className="absolute right-0 bottom-full mb-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" role="listbox" aria-label="Select assignee" data-id="element-110">
              {ASSIGNEES.map(assignee => <button key={assignee} onClick={e => {
            e.stopPropagation();
            handleSelectAssignee(assignee);
          }} onPointerDown={e => e.stopPropagation()} className={`
                    w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50
                    ${condition.assignee === assignee ? 'bg-blue-50' : ''}
                  `} role="option" aria-selected={condition.assignee === assignee} data-id="element-111">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white ${ASSIGNEE_COLORS[assignee] || 'bg-gray-400'}`} data-id="element-112">
                    {getInitials(assignee)}
                  </span>
                  <span className="flex-1" data-id="element-113">{assignee}</span>
                  {condition.assignee === assignee && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-114" />}
                </button>)}
            </div>}
        </div>
      </div>
    </div>;
}
export function ConditionCardOverlay({
  condition
}: {
  condition: Condition;
}) {
  const dueDateStatus = getDueDateStatus(condition.dueDate);
  return <div className="bg-white rounded-lg border-2 border-blue-400 p-3 shadow-2xl w-[280px] rotate-2" data-id="element-115">
      <div className="flex items-start justify-between mb-2" data-id="element-116">
        <CategoryBadge category={condition.category} data-id="element-117" />
      </div>
      <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-3 leading-snug" data-id="element-118">
        {condition.title}
      </h3>
      <div className="flex items-center justify-between" data-id="element-119">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${dueDateStatus.className}`} data-id="element-120">
          <CalendarIcon className="w-3 h-3" data-id="element-121" />
          {dueDateStatus.label}
        </span>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${ASSIGNEE_COLORS[condition.assignee] || 'bg-gray-400'}`} data-id="element-122">
          {getInitials(condition.assignee)}
        </div>
      </div>
    </div>;
}